import { useTranslation, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { useCallback, useEffect, useRef, useState, type ComponentProps } from "react";
import { markReviewMapper, reviewStageMapper } from "./ReviewsConstants";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import { convertDate } from "@Services/luxonHelper";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import {
  canUserClearReviewLevel,
  getReviewData,
  notifyUserInReview,
  saveUserResponse,
  userPermissionsToMarkResponse,
} from "./ReviewsHelper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ReviewLevelTree from "@Components/SpaceSettings/UserAdministration/ReviewFlows/ReviewFlowReviewLevels/ReviewLevelTree";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MarkReviewModal from "./MarkReviewModal";
import ReviewLevelIcon from "@Assets/icons/svg/ReviewLevel.svg";
import { getNotificationsCount } from "@Actions/notification";
import { ApproveReviewIcon, SkipReviewIcon } from "@Components/StickySidebarViewer/assets/ReviewIcons";
import ReviewBasicDetails from "./ReviewBasicDetails";
import { fetchReviewData } from "@Actions/ReviewFlows";
import { setReviewData } from "@Reducers/ReviewFlows";
import type {
  ReviewDataResult,
  NotifyUserInReviewRequest,
  SaveUserResponseForReviewRequest,
  UserPermissionsToMarkReviewResponse,
} from "@ServerTypes/reviews/reviews.schema";
import ReviewNotificationModal from "./ReviewNotificationModal";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import ModuleUsersAccess from "@Components/ModuleUsersAccess";
import { defaultModulesMapper } from "@Helpers/constants";
import { RootState } from "@Reducers/index";
import type { ReviewApprovalStage } from "./ReviewsHelper";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** The three collapsible sections this panel can render, in caller-chosen order. */
type ReviewAccordionKey = "details" | "access" | "reviewFlow";

/** A reviewer entry of the review's embedded reviewFlow levels (reviewflows.schema creatorSchema). */
type ReviewFlowLevelReviewer = NonNullable<
  NonNullable<NonNullable<ReviewDataResult["reviewFlow"]>["reviewLevels"]>[number]["reviewers"]
>[number];

/**
 * Payload `ReviewNotificationModal` hands its `confirmCallback`. `message` is the modal's own field
 * name; the two reviewer-id arrays are exactly `saveUserResponseForReviewRequest`'s.
 */
type ReviewNotificationConfirmData = Pick<
  SaveUserResponseForReviewRequest,
  "selectedReviewerIdsFromSpace" | "selectedReviewerIdsAtTargetLevel"
> & { message?: string };

/** Props `ReviewNotificationModal` is spread with, assembled by `initializeUserResponse`. */
interface ReviewNotificationPayload {
  spaceId?: string;
  reviewId: ObjectIdString;
  modalTitle: string;
  targetLevelReviewers?: ReviewFlowLevelReviewer[];
  targetLevelSerial?: number;
}

interface ReviewNotificationState {
  show: boolean;
  action: string | null;
  data: ReviewNotificationPayload | null;
}

interface UserCommentModalState {
  show: boolean;
  type: string | null;
  comment?: string;
}

interface ReviewPropertiesProps {
  /** Review `_id` — `objectIdString` in every reviews.schema.ts request this panel issues. */
  readonly _id: ObjectIdString;
  /** `ModuleUsersAccess` declares `spaceId?: string`; ReviewsList sources it from an untyped grid row. */
  readonly spaceId?: string;
  /** ReviewDetails renders this panel inline and passes `null` for both callbacks. */
  readonly closeProperties: (() => void) | null;
  readonly refreshGrid: (() => void) | null;
  readonly allowFetchData?: boolean;
  readonly showHeader?: boolean;
  readonly width?: string;
  readonly accordionOrder?: ReviewAccordionKey[];
}

function ReviewProperties({
  closeProperties,
  _id,
  refreshGrid,
  spaceId,
  allowFetchData = true,
  showHeader = true,
  width = "32.5rem",
  accordionOrder = ["details", "access", "reviewFlow"],
}: ReviewPropertiesProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const menuBtnRef = useRef<HTMLElement | null>(null);

  const panelStyle = { width, minWidth: width };
  const baseStatus = { status: false, errorMessage: "Permission denied" };
  const defaultReviewNotificationData: ReviewNotificationState = {
    show: false,
    action: null,
    data: null,
  };
  const initialState: UserPermissionsToMarkReviewResponse = {
    skip: baseStatus,
    revoke: baseStatus,
    approve: baseStatus,
    rollback: baseStatus,
  };

  const [enableBtns, setEnableBtns] = useState(initialState);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [canUserClearCurrentLevel, setCanUserClearCurrentLevel] = useState(false);
  const [userCommentModal, setUserCommentModal] = useState<UserCommentModalState>({
    show: false,
    type: null,
  });
  // Partial: `openDetailsOnly` / `openReviewFlowOnly` replace the map with two keys, dropping
  // `access` — preserved verbatim, the Access accordion then reads `undefined` and stays collapsed.
  const [accordionState, setAccordionState] = useState<Partial<Record<ReviewAccordionKey, boolean>>>({
    details: true,
    reviewFlow: true,
    access: true,
  });
  const [reviewNotification, setReviewNotification] = useState<ReviewNotificationState>(defaultReviewNotificationData);

  const reviewData = useSelector((state: RootState) => state.reviewData) as ReviewDataResult & {
    approvalStages: NonNullable<ReviewDataResult["approvalStages"]>;
    reviewStatus: NonNullable<ReviewDataResult["reviewStatus"]>;
    reviewFlow: NonNullable<ReviewDataResult["reviewFlow"]>;
  };

  const menuItems: Array<{ id: string; text: string; disabled?: boolean }> = [
    { id: "1", text: "Close" },
    { id: "2", text: "Revoke", disabled: !enableBtns.revoke.status },
  ];

  const openDetailsOnly = () => {
    setAccordionState({ details: true, reviewFlow: false });
  };

  const openReviewFlowOnly = () => {
    setAccordionState({ details: false, reviewFlow: true });
  };

  const closeReviewNotificationModal = () => {
    setReviewNotification(defaultReviewNotificationData);
  };

  const selectItem = (e: { item: { id?: string } }) => {
    if (e.item.id === "1" && closeProperties) {
      closeProperties();
    }

    if (e.item.id === "2") {
      initializeUserResponse("revoke");
    }
  };

  const closeUserCommentModal = () => {
    setUserCommentModal({ show: false, type: null });
  };

  // const notifyUsersInLevelCallback = async (levelId) => {
  //   const response = await notifyUsersInLevel({ levelId, reviewId: _id });

  //   if (response.data.status) {
  //     dispatch(showToastNotification({ content: t("The user(s) have been notified"), type: TOAST_TYPE_SUCCESS }));
  //     dispatch(getNotificationsCount(['NOTIFICATION_UNREAD']));
  //     return;
  //   }

  //   const content = response.data.errorMessage || 'The user(s) could not be notified';
  //   dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
  // };

  const notifyUserInReviewCallback = async (
    levelId: NotifyUserInReviewRequest["levelId"],
    reviewerId: NotifyUserInReviewRequest["reviewerId"],
  ) => {
    const response = await notifyUserInReview({
      levelId,
      reviewId: _id,
      reviewerId,
    });

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The user has been notified"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
      dispatch(fetchReviewData({ reviewId: _id }));
      return;
    }

    const content = untranslated(response.data.errorMessage || "The user could not be notified");
    dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
  };

  const fetchData = useCallback(async (reviewId: ObjectIdString) => {
    const promiseFetchReview = getReviewData({ _id: reviewId });
    const promiseFetchPermissions = userPermissionsToMarkResponse({ reviewId });
    const promiseCanUserClearCurrentLevel = canUserClearReviewLevel({
      reviewId,
    });

    const [fetchReviewResponse, permissionsResponse, canUserClearCurrentLevelResponse] = await Promise.all([
      promiseFetchReview,
      promiseFetchPermissions,
      promiseCanUserClearCurrentLevel,
    ]);

    if (fetchReviewResponse.data.status) {
      dispatch(setReviewData(fetchReviewResponse.data.result as ReviewDataResult));
    }

    if (permissionsResponse.data) {
      const { skip, revoke, approve, rollback } = permissionsResponse.data;
      const data = {
        skip,
        approve,
        revoke,
        rollback,
      };
      setEnableBtns((prev) => ({ ...prev, ...data }));
    }

    if (canUserClearCurrentLevelResponse.data) {
      const value = !!canUserClearCurrentLevelResponse.data.status;
      setCanUserClearCurrentLevel(value);
    }

    setIsLoadingComplete(true);
  }, []);

  const finalizeUserResponse = async (userResponse: string, userComment: string) => {
    const { _id: reviewId } = reviewData;
    if (!reviewId) {
      return;
    }
    const response = await saveUserResponse({
      reviewId,
      userResponse,
      userComment,
    });

    setUserCommentModal({ show: false, type: null });

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: markReviewMapper[userResponse as keyof typeof markReviewMapper].successToast,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      await fetchData(reviewId);

      if (refreshGrid) {
        refreshGrid();
      }

      return;
    }

    dispatch(
      showToastNotification({
        content:
          untranslated(response.data.errorMessage) ||
          markReviewMapper[userResponse as keyof typeof markReviewMapper].failureDefaultToast,
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const approveAndClearReviewLevel = async (data: ReviewNotificationConfirmData) => {
    const { message: userComment, selectedReviewerIdsFromSpace, selectedReviewerIdsAtTargetLevel } = data;
    const userResponse = "approve";
    const approveReviewResponse = await saveUserResponse({
      reviewId: _id,
      userResponse,
      userComment,
      selectedReviewerIdsFromSpace,
      selectedReviewerIdsAtTargetLevel,
    });

    closeReviewNotificationModal();

    if (approveReviewResponse.data.status) {
      dispatch(
        showToastNotification({
          content: t("The review has been approved and selected users have been notified"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      await fetchData(_id);

      if (refreshGrid) {
        refreshGrid();
      }

      return;
    }

    dispatch(
      showToastNotification({
        // KNOWN BUG, preserved verbatim: this branch renders the raw server `errorMessage` without
        // `untranslated()` (unlike every sibling toast in this file and ReviewDetails), so it is not
        // marked translated. The cast is erased; adding `untranslated()` would emit a call.
        content:
          (approveReviewResponse.data.errorMessage as TranslatedString) ||
          markReviewMapper[userResponse as keyof typeof markReviewMapper].failureDefaultToast,
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const initializeUserResponse = (userResponse: string) => {
    if (userResponse === "approve" && canUserClearCurrentLevel) {
      const { currentReviewLevel } = reviewData;
      const currentReviewLevelIndex = currentReviewLevel ? currentReviewLevel.serial - 1 : undefined;
      const targetReviewLevel =
        currentReviewLevelIndex === undefined
          ? undefined
          : reviewData.reviewFlow.reviewLevels?.[currentReviewLevelIndex + 1];

      setReviewNotification({
        show: true,
        action: "clearReviewLevel",
        data: {
          spaceId,
          reviewId: _id,
          modalTitle: "Approve & Notify Users",
          targetLevelReviewers: targetReviewLevel?.reviewers?.filter(
            (record: ReviewFlowLevelReviewer) => !record.isUserDeleted,
          ),
          targetLevelSerial: targetReviewLevel?.serial,
        },
      });

      return;
    }

    setUserCommentModal({ show: true, comment: "", type: userResponse });
  };

  const unmountReview = useCallback(() => {
    dispatch(setReviewData({}));
  }, []);

  useEffect(() => {
    if (allowFetchData) {
      fetchData(_id);
    } else {
      setIsLoadingComplete(true);
    }

    return unmountReview();
  }, [_id, allowFetchData, fetchData, unmountReview]);

  const getUserAvatarComponent = (user: NonNullable<ReviewDataResult["createdBy"]>) => (
    <UserNameTagComponent id={user.userId} />
  );

  const detailsHeader = () => (
    <div className="py-2.5 px-4 flex flex-row items-center justify-start gap-2 [&_span]:font-bold [&_span]:text-base">
      <DetailsIcon size="24" color="#192048" />
      <span>{t("Details")}</span>
    </div>
  );

  const detailsContent = () => (
    <ReviewBasicDetails
      reviewFlowName={reviewData.reviewFlow.name}
      createdBy={reviewData.createdBy}
      currentReviewLevelSerial={reviewData.currentReviewLevel?.serial}
      currentReviewLevelTitle={reviewData.currentReviewLevel?.title}
      nextActionDueDate={reviewData.nextActionDueDate}
      createdDate={reviewData.createdDate}
      finishedDate={reviewData.finishedDate}
      numberOfEntitiesAttached={reviewData.numberOfEntitiesAttached}
    />
  );

  const reviewFlowHeader = () => (
    <div className="py-2.5 px-4 flex flex-row items-center justify-start gap-2 [&_span]:font-bold [&_span]:text-base">
      <img src={ReviewLevelIcon} />
      <span>{t("Review Flow")}</span>
    </div>
  );

  const accessHeader = () => (
    <div className="py-2.5 px-4 flex flex-row items-center justify-start gap-2 [&_span]:font-bold [&_span]:text-base">
      <IconComponent name="access" width="1.5rem" height="1.5rem" />
      <span>{t("Access")}</span>
    </div>
  );

  const accessContent = () => (
    <div className="px-4 pb-4">
      <ModuleUsersAccess
        module={defaultModulesMapper.REVIEWS}
        resourceId={_id}
        spaceId={spaceId}
        users={(reviewData.users ?? []) as unknown as ComponentProps<typeof ModuleUsersAccess>["users"]}
      />
    </div>
  );

  const reviewFlowContent = () => (
    <div className="ps-0">
      <ReviewLevelTree
        reviewLevels={reviewData.approvalStages.map((record: ReviewApprovalStage) => ({
          ...record,
          _id: record.levelId,
        }))}
        showProgress
        showNotifyBtn
        notifyUserInReviewCallback={notifyUserInReviewCallback}
      />
    </div>
  );

  const getDetailsAccordion = () => (
    <Accordion
      disableGutters
      expanded={accordionState.details}
      onChange={() => setAccordionState((prev) => ({ ...prev, details: !prev.details }))}
      className="shadow-none border-b-0.5 border-[#ecf3fe]"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className="p-0">
        {detailsHeader()}
      </AccordionSummary>
      <AccordionDetails>{detailsContent()}</AccordionDetails>
    </Accordion>
  );

  const getReviewFlowAccordion = () => (
    <Accordion
      disableGutters
      expanded={accordionState.reviewFlow}
      onChange={() => setAccordionState((prev) => ({ ...prev, reviewFlow: !prev.reviewFlow }))}
      className="shadow-none border-b-0.5 border-[#ecf3fe]"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className="p-0">
        {reviewFlowHeader()}
      </AccordionSummary>
      <AccordionDetails>{reviewFlowContent()}</AccordionDetails>
    </Accordion>
  );
  const getAccessAccordion = () => (
    <Accordion
      disableGutters
      expanded={accordionState.access}
      onChange={() => setAccordionState((prev) => ({ ...prev, access: !prev.access }))}
      className="shadow-none border-b-0.5 border-[#ecf3fe]"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className="p-0">
        {accessHeader()}
      </AccordionSummary>
      <AccordionDetails>{accessContent()}</AccordionDetails>
    </Accordion>
  );

  const getDetailsIcon = () => (
    <DetailsIcon size="24" color="#192048" onClick={openDetailsOnly} className="cursor-pointer" />
  );

  const getReviewFlowIcon = () => <img src={ReviewLevelIcon} onClick={openReviewFlowOnly} className="cursor-pointer" />;

  const getAccessIcon = () => <IconComponent name="access" width="1.5rem" height="1.5rem" />;

  const getAccordionComponent = () => (
    <>
      {accordionOrder.map((record: ReviewAccordionKey) => {
        const { accordion } = accordionOrderMapper[record];
        return accordion();
      })}
    </>
  );

  const accordionOrderMapper: Record<
    ReviewAccordionKey,
    { accordion: () => React.JSX.Element; icon: () => React.JSX.Element }
  > = {
    details: {
      accordion: getDetailsAccordion,
      icon: getDetailsIcon,
    },
    access: {
      accordion: getAccessAccordion,
      icon: getAccessIcon,
    },
    reviewFlow: {
      accordion: getReviewFlowAccordion,
      icon: getReviewFlowIcon,
    },
  };

  const getMarkReviewModal = () => {
    const { approvalStages } = reviewData;
    const currentStage = approvalStages.find((stage: ReviewApprovalStage) => stage.clearanceStatus === "in_progress");

    if (currentStage) {
      const { requireMandatoryComment } = currentStage;
      const actionMap = { approve: 1, revoke: 2, skip: 3 };
      const userAction = actionMap[userCommentModal.type as keyof typeof actionMap];

      const isCommentRequired =
        requireMandatoryComment?.enabled && requireMandatoryComment?.values?.includes(userAction);

      return (
        <MarkReviewModal
          requireMandatoryComment={isCommentRequired}
          closeModal={closeUserCommentModal}
          userResponse={userCommentModal.type!}
          finalizeUserResponse={finalizeUserResponse}
        />
      );
    }
  };

  const getReviewNotificationModal = () => {
    const { approvalStages } = reviewData;
    const currentStage = approvalStages.find((stage: ReviewApprovalStage) => stage.clearanceStatus === "in_progress");

    const requireMandatoryComment = currentStage
      ? currentStage.requireMandatoryComment?.enabled && currentStage.requireMandatoryComment?.values?.includes(1)
      : false;

    return (
      <ReviewNotificationModal
        {...reviewNotification.data}
        action={reviewNotification.action}
        approvalStages={reviewData.approvalStages}
        confirmCallback={approveAndClearReviewLevel}
        cancelCallback={closeReviewNotificationModal}
        requireMandatoryComment={requireMandatoryComment}
      />
    );
  };

  return (
    <>
      {isLoadingComplete && (
        <div className="flex flex-col bg-white h-full overflow-hidden" style={panelStyle}>
          {userCommentModal.show && getMarkReviewModal()}
          {reviewNotification.show && reviewNotification.action === "clearReviewLevel" && getReviewNotificationModal()}
          {showHeader ? (
            <div className="flex flex-col gap-2 pt-4 pb-6 border-b-0.5 border-[#ecf3fe]">
              <div className="flex flex-row items-center justify-between py-2 pe-6 ps-8">
                <div className="py-2 px-3 border border-[#192048] rounded-sm">
                  {t("RE -")} {reviewData.serialId}
                </div>

                <div className="flex flex-row items-center justify-start">
                  <div
                    className={`flex flex-row items-center w-fit justify-start gap-2 py-1 px-3 rounded-2xl ${
                      reviewStageMapper[reviewData.reviewStatus].containerClass === "reviewStageClosed"
                        ? "bg-[rgb(69_207_128_/_25%)]"
                        : reviewStageMapper[reviewData.reviewStatus].containerClass === "reviewStageRevoked"
                          ? "bg-[rgb(231_85_85_/_25%)]"
                          : reviewStageMapper[reviewData.reviewStatus].containerClass === "reviewStageOpen"
                            ? "bg-[rgb(255_207_85_/_25%)]"
                            : "bg-[rgb(102_106_132_/_25%)]"
                    }`}
                  >
                    <div
                      className={`rounded-full h-3 w-3 ${
                        reviewStageMapper[reviewData.reviewStatus].indicatorClass === "reviewStageClosedIndicator"
                          ? "bg-[#45cf80]"
                          : reviewStageMapper[reviewData.reviewStatus].indicatorClass === "reviewStageRevokedIndicator"
                            ? "bg-[#e75555]"
                            : reviewStageMapper[reviewData.reviewStatus].indicatorClass === "reviewStageOpenIndicator"
                              ? "bg-[#ffcf55]"
                              : "bg-[#666a84]"
                      }`}
                    />
                    <p className="text-sm font-semibold">{reviewStageMapper[reviewData.reviewStatus].name}</p>
                  </div>
                  <SelectDropDown
                    select={selectItem}
                    ref={menuBtnRef}
                    items={menuItems}
                    cssClass="e-caret-hide border-0 ms-6"
                  >
                    <span className="e-more-vertical-1 e-icons" />
                  </SelectDropDown>
                </div>
              </div>

              {reviewData.currentReviewLevel && (
                <div className="py-2 pe-6 ps-8">
                  <div className="py-1 px-4 flex flex-row items-center justify-start gap-2 bg-[#f0f5f9] rounded">
                    <DetailsIcon size="24" color="#192048" />
                    {reviewData.currentReviewLevel ? (
                      <p>{`Waiting for reviewer at level ${reviewData.currentReviewLevel.serial}`}</p>
                    ) : (
                      <p>{t("This review is not active")}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="py-2 pe-6 ps-8 [&_span]:text-2xl [&_span]:font-semibold">
                <span>{reviewData.name}</span>
              </div>

              <div className="flex flex-row items-center justify-start gap-2 py-2 pe-6 ps-8">
                {/* `createdBy` is optional in reviewDataResultSchema but the service always writes it
                    on create; this dereference is unguarded today. `!` is erased. */}
                {getUserAvatarComponent(reviewData.createdBy!)}
                <span>{t("on")}</span>
                <span className="font-bold">{convertDate(Number(reviewData.createdDate), "dateForText")}</span>
              </div>

              <div className="py-2 pe-6 ps-8 flex flex-row items-center justify-start gap-4">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  onClick={() => initializeUserResponse("approve")}
                  disabled={!enableBtns.approve.status}
                >
                  <div className="flex flex-row items-center justify-start gap-2">
                    <ApproveReviewIcon color={enableBtns.approve.status ? "#192048" : "#13143080"} />
                    {t("Mark As Reviewed")}
                  </div>
                </ButtonComponent>
                {enableBtns.skip.status && (
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={() => initializeUserResponse("skip")}
                    disabled={!enableBtns.skip.status}
                  >
                    <div className="flex flex-row items-center justify-start gap-2">
                      <SkipReviewIcon />
                      {t("Skip")}
                    </div>
                  </ButtonComponent>
                )}
              </div>
            </div>
          ) : undefined}
          <div className="flex flex-row h-full overflow-auto">
            <div className="w-full overflow-auto flex flex-col">{reviewData._id && getAccordionComponent()}</div>
            <div className="py-6 px-[1.125rem] flex flex-col gap-6 border-s-0.5 border-[#ecf3fe] bg-white">
              {accordionOrder.map((record: ReviewAccordionKey) => {
                const { icon } = accordionOrderMapper[record];
                return icon();
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ReviewProperties;
